// 05-11 (auditoría, QA G3) — la lista NO se mueve bajo el puntero. El orden se recalcula con cada aviso del socket: con el
// ratón sobre «Sí» de un agente, llegaba un permiso más antiguo de otro, su fila subía encima y el mismo clic daba permiso
// a quien Pau no había leído. Como Correo: mientras el puntero está sobre las filas (o una fila tiene el foco del
// teclado) el orden se queda QUIETO: las filas que ya estaban no cambian de sitio ni de bloque (su contenido sí se pone al
// día: un «Sí» ya dado desaparece) y las que llegan nuevas esperan a que se suelte. Se suelta al salir el puntero / irse
// el foco (con `SOLTAR_TRAS_MS` de margen, para cruzar un hueco sin que salte) y nunca dura más de `CONGELAR_MAX_MS`.
//
// Fuente única del orden: sigue siendo useEquipo (lista, riel, Ctrl+K y Alt+↑/↓ leen lo mismo); aquí solo se guarda la
// FOTO del orden que se veía al congelar y useEquipo la aplica encima. Alt+↓ recorre por tanto lo que se ve.
// Lo que no se puede congelar: un agente que se va de la oficina desaparece aunque esté congelado (no queda nada que
// pintar); los que se van pasan antes por «Se van», así que casi nunca ocurre bajo el puntero.
import { create } from 'zustand';
import type { ContratadoDTO } from '../compartido/vocabulario';
import { BLOQUES, type Bloque, type GrupoConMiembros } from './situacion';

export const CONGELAR_MAX_MS = 10_000;
export const SOLTAR_TRAS_MS = 400;
/**
 * 05-11 (revisión de la integración 15): al tope de 10 s la lista se pone al día de golpe aunque el puntero siga encima;
 * un clic justo en ese instante podía caer en otra fila. Durante este rato tras recolocarla así, los botones de acción de
 * las filas («Sí», «Revisar», «Cerrar») no hacen nada: Pau ve el orden nuevo antes de que su clic cuente.
 */
export const ASENTAR_MS = 700;
let recolocadaEn = 0;

/** ¿Se acaba de recolocar la lista con el puntero encima? (los botones de las filas esperan a que se asiente) */
export function listaRecienRecolocada(ahora = Date.now()): boolean {
  const desde = ahora - recolocadaEn;
  return desde >= 0 && desde < ASENTAR_MS; // un reloj que va hacia atrás (cambio de hora) no bloquea para siempre
}

/** El orden que se veía al congelar, en la bandeja `clave` (cambiar de bandeja no se congela). */
export interface FotoOrden {
  clave: string;
  orden: readonly { agentId: string; bloque: Bloque }[];
}

export const useOrdenQuieto = create<{ foto: FotoOrden | null }>(() => ({ foto: null }));

type Origen = 'puntero' | 'foco';
const sujetan = new Set<Origen>();
let relojSoltar: ReturnType<typeof setTimeout> | undefined;
let relojTope: ReturnType<typeof setTimeout> | undefined;

/** Congela el orden (si no lo estaba ya) con la foto de lo que se ve ahora. */
export function congelarOrden(origen: Origen, foto: () => FotoOrden): void {
  clearTimeout(relojSoltar);
  sujetan.add(origen);
  if (useOrdenQuieto.getState().foto) return;
  useOrdenQuieto.setState({ foto: foto() });
  relojTope = setTimeout(() => descongelarOrden(true), CONGELAR_MAX_MS);
}

/** Este origen deja de sujetar el orden; cuando no queda ninguno, se suelta tras un momento. */
export function soltarOrden(origen: Origen): void {
  sujetan.delete(origen);
  if (sujetan.size || !useOrdenQuieto.getState().foto) return;
  clearTimeout(relojSoltar);
  relojSoltar = setTimeout(descongelarOrden, SOLTAR_TRAS_MS);
}

/** `porTope`: lo suelta el tope de 10 s (quizá con alguien todavía encima: las filas pueden moverse bajo el puntero). */
export function descongelarOrden(porTope = false): void {
  clearTimeout(relojSoltar);
  clearTimeout(relojTope);
  if (porTope && sujetan.size && useOrdenQuieto.getState().foto) recolocadaEn = Date.now();
  sujetan.clear();
  if (useOrdenQuieto.getState().foto) useOrdenQuieto.setState({ foto: null });
}

/** La foto de unos bloques (lo que se ve). */
export function fotoDe(clave: string, grupos: readonly GrupoConMiembros<Pick<ContratadoDTO, 'agentId'>>[]): FotoOrden {
  return { clave, orden: grupos.flatMap((g) => g.miembros.map((c) => ({ agentId: c.agentId, bloque: g.bloque }))) };
}

/**
 * PURO. Los bloques vivos con el orden de la foto: cada uno de los que estaban, en su bloque y su sitio de entonces (con
 * sus datos de ahora); los que ya no están, fuera; los nuevos, esperando a que se suelte. Sin foto (o de otra bandeja),
 * los vivos tal cual.
 */
export function aplicarOrdenQuieto<C extends Pick<ContratadoDTO, 'agentId'>>(grupos: GrupoConMiembros<C>[], foto: FotoOrden | null, clave: string): GrupoConMiembros<C>[] {
  if (!foto || foto.clave !== clave) return grupos;
  const vivos = new Map(grupos.flatMap((g) => g.miembros.map((c) => [c.agentId, c] as const)));
  const porBloque = new Map<Bloque, C[]>();
  for (const { agentId, bloque } of foto.orden) {
    const c = vivos.get(agentId);
    if (!c) continue;
    const suyos = porBloque.get(bloque);
    if (suyos) suyos.push(c);
    else porBloque.set(bloque, [c]);
  }
  return BLOQUES.map((bloque) => ({ bloque, miembros: porBloque.get(bloque) ?? [] })).filter((g) => g.miembros.length > 0);
}

function esFocoDeTeclado(el: EventTarget): boolean {
  try {
    return el instanceof Element && el.matches(':focus-visible');
  } catch {
    return true; // un navegador sin :focus-visible: mejor congelar de más
  }
}

/**
 * Las asas para el contenedor de las filas: el puntero encima (ratón, lápiz o dedo) y el foco del teclado dentro lo
 * congelan; salir lo suelta. `foto` = lo que se ve en este momento.
 */
export function asasOrdenQuieto(foto: () => FotoOrden) {
  return {
    onPointerEnter: () => congelarOrden('puntero', foto),
    // Tras el tope de 10 s, moverse otra vez por encima vuelve a congelar (con la foto de ese momento).
    onPointerMove: () => congelarOrden('puntero', foto),
    onPointerLeave: () => soltarOrden('puntero'),
    // Solo el foco del teclado (Tab, o el que vuelve al cerrar el chat con Esc): el del clic ya lo cubre el puntero.
    onFocus: (e: { target: EventTarget }) => esFocoDeTeclado(e.target) && congelarOrden('foco', foto),
    onBlur: (e: { relatedTarget: EventTarget | null; currentTarget: EventTarget & Element }) => {
      if (!(e.relatedTarget instanceof Node && e.currentTarget.contains(e.relatedTarget))) soltarOrden('foco');
    },
  };
}
